import { Directive, computed, effect, inject, input } from '@angular/core';

import { DsNotificationBubble } from '@frontend/ui/notification-bubble';
import { MenuItemsService } from '@frontend/vanilla/core';

/**
 * @stable
 */
export enum BadgeType {
    Counter = 'counter',
    Icon = 'icon',
}

/**
 * @whatItDoes Displays a counter or icon for specified item.
 *
 * @howToUse
 *
 * ```
 * <span vnMenuItemBadge section="Header" item="inbox" badgeClass="theme-spot-filled" badgeType="counter"></span>
 * ```
 *
 * @description
 *
 * {@link BadgeType.Counter} is displayed based on the value set by {@link MenuItemsService}.
 *
 * @param section string to indicate in which feature the badge is part of: e.g. {@link MenuSection.Header}.
 * @param item string to indicate the badge name.
 * @param badgeClass string indicate if the badge should be displays as dot without counter. Use class 'theme-spot-filled'.
 * @param badgeType string | undefined to indicate the badge type: e.g. {@link BadgeType.Icon}; {@link BadgeType.Counter} by default. If `badgeType` is provided, badge will be always visible.
 *
 * @stable
 */
@Directive({
    standalone: true,
    selector: 'ds-notification-bubble[vnMenuItemBadge]',
    host: {
        '[attr.hidden]': 'hidden()',
    },
})
export class MenuItemBadgeDirective {
    private menuItemsService = inject(MenuItemsService);

    readonly section = input<string>();
    readonly item = input<string>();
    readonly badgeClass = input<string | string[]>();
    readonly badgeType = input<string>();

    private notificationBubble = inject(DsNotificationBubble);

    private counter = computed(() => {
        const section = this.section();
        const item = this.item();
        if (section && item) {
            const result = this.menuItemsService.getCounter(section, item);
            return result ? { ...result } : null;
        }
        return null;
    });

    readonly hidden = computed(() => {
        const counter = this.counter();
        const badgeType = counter?.type || this.badgeType() || BadgeType.Counter;
        return (badgeType === BadgeType.Icon ? !counter?.cssClass : !counter?.count) || null;
    });

    readonly label = computed(() => {
        const counter = this.counter();
        const badgeClass = this.badgeClass();

        const badgeType = counter?.type || this.badgeType() || BadgeType.Counter;

        if (badgeType === BadgeType.Counter) {
            if (badgeClass?.includes('theme-spot-filled') || counter?.count === undefined) {
                return '';
            } else {
                return counter?.count;
            }
        }
        return '';
    });

    constructor() {
        effect(() => {
            this.notificationBubble.label.set(this.label());
        });
    }
}
